export function format(date, type = 'datetime') {
  if (!window.stzhComponents.translations) {
    console.log('Translations not loaded yet, check for Promise `window.stzhComponents.translationsLoading`');
    return;
  }

  const { $formats, $globals } = window.stzhComponents.translations;
  const adapter = createFormatParseAdapter($formats, $globals);
  return adapter.format(date, type);
}

export function parse(string, type = 'datetime') {
  if (!window.stzhComponents.translations) {
    console.log('Translations not loaded yet, check for Promise `window.stzhComponents.translationsLoading`');
    return;
  }

  const { $formats, $globals } = window.stzhComponents.translations;
  const adapter = createFormatParseAdapter($formats, $globals);
  return adapter.parse(string, type);
}

export function formatSpan(date, date2, type = 'datetime') {
  if (!window.stzhComponents.translations) {
    console.log('Translations not loaded yet, check for Promise `window.stzhComponents.translationsLoading`');
    return;
  }

  const { $formats, $globals } = window.stzhComponents.translations;
  const adapter = createFormatParseAdapter($formats, $globals);
  return adapter.formatSpan(date, date2, type);
}

export function parseSpan(string, type = 'datetime') {
  if (!window.stzhComponents.translations) {
    console.log('Translations not loaded yet, check for Promise `window.stzhComponents.translationsLoading`');
    return;
  }

  const { $formats, $globals } = window.stzhComponents.translations;
  const adapter = createFormatParseAdapter($formats, $globals);
  return adapter.parseSpan(string, type);
}

export function createFormatParseAdapter(formats, globals) {
  // format letters from https://www.php.net/manual/de/datetime.format.php
  const globalFormatter = function (format, date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    let j = date.getDate().toString(10);
    let d = j;
    let n = (date.getMonth() + 1).toString(10);
    let m = n;
    let yC = date.getFullYear().toString(10);
    let gC = date.getHours().toString(10);
    let hC = gC;
    let i = date.getMinutes().toString(10);

    // full textual representation of a month
    let fC = globals.monthNames[date.getMonth()];
    // short textual representation of a month
    let mC = globals.monthNamesShort[date.getMonth()];
    // full textual representation of the day
    let l = globals.dayNames[date.getDay()];
    // short textual representation of the day
    let D = globals.dayNamesShort[date.getDay()];

    // months *are* zero-indexed, pad if less than 9!
    if (date.getMonth() < 9) {
      m = `0${m}`;
    }

    if (date.getDate() < 10) {
      d = `0${d}`;
    }

    if (date.getHours() < 10) {
      hC = `0${hC}`;
    }

    if (date.getMinutes() < 10) {
      i = `0${i}`;
    }

    return format
      .replace(/\{j\}/g, j)
      .replace(/\{d\}/g, d)
      .replace(/\{D\}/g, D)
      .replace(/\{l\}/g, l)
      .replace(/\{n\}/g, n)
      .replace(/\{m\}/g, m)
      .replace(/\{F\}/g, fC)
      .replace(/\{M\}/g, mC)
      .replace(/\{Y\}/g, yC)
      .replace(/\{H\}/g, hC)
      .replace(/\{G\}/g, gC)
      .replace(/\{i\}/g, i);
  };

  const globalParser = function (parse, input) {
    if (!input) {
      return;
    }

    const regex = new RegExp(parse);
    const match = input.match(regex);

    if (match) {
      const { groups } = match;

      const year = parseInt(groups.Y || '', 10);
      const month = parseInt(groups.n || groups.m || '', 10);
      const day = parseInt(groups.j || groups.d || '', 10);
      const hours = parseInt(groups.G || groups.H || '', 10);
      const minutes = parseInt(groups.i || '', 10);

      if (!isNaN(year) && (year < 1000 || year > 9999)) {
        return;
      }

      if (!isNaN(month) && (month < 1 || month > 12)) {
        return;
      }

      // we check day later again (if month changed, it was out of range)
      if (!isNaN(day) && (day < 1 || day > 31)) {
        return;
      }

      if (!isNaN(hours) && (hours < 0 || hours > 23)) {
        return;
      }

      if (!isNaN(minutes) && (minutes < 0 || minutes > 59)) {
        return;
      }

      const date = new Date(
        !isNaN(year) ? year : null,
        !isNaN(month) ? month - 1 : null,
        !isNaN(day) ? day : null,
        !isNaN(hours) ? hours : null,
        !isNaN(minutes) ? minutes : null
      );

      // if day was out of possible range (1 - 28/29/30/31)
      // we find out by check if month has changed
      if (date.getMonth() + 1 !== month) {
        return;
      }

      return date;
    }
  };

  const globalAdapter = {
    format: function (date, type = 'datetime') {
      const dateFormats = {
        ...formats[type],
        ...(window.stzhComponents?.formats[type] || {})
      };

      if (typeof dateFormats.format === 'string') {
        return globalFormatter(dateFormats.format, date);
      } else {
        return dateFormats.format(date);
      }
    },
    parse: function (input, type = 'datetime') {
      const dateFormats = {
        ...formats[type],
        ...(window.stzhComponents?.formats[type] || {})
      };

      if (typeof dateFormats.parse === 'string') {
        return globalParser(dateFormats.parse, input);
      } else {
        return dateFormats.parse(input);
      }
    },
    formatSpan: function (date, date2, type = 'datetime') {
      let formattedDate = date;
      let formattedDate2 = date2;

      if (date instanceof Date && !isNaN(date.getTime())) {
        formattedDate = this.format(date, type);
      }

      if (date2 instanceof Date && !isNaN(date2.getTime())) {
        formattedDate2 = this.format(date2, type);
      }

      let range = globals.dateRange;

      if (type.startsWith('time')) {
        range = globals.timeRange;
      }

      return (
        formattedDate +
        (formattedDate2 &&
        (!(formattedDate2 instanceof Date) || (formattedDate2 instanceof Date && !isNaN(date.getTime())))
          ? range + formattedDate2
          : '')
      );
    },
    parseSpan: function (string, type) {
      let range = globals.dateRange;

      if (type.startsWith('time')) {
        range = globals.timeRange;
      }

      const dates = string.split(range);
      const date = this.parse(dates[0], type);
      const date2 = this.parse(dates[1], type);

      return [date, date2, range];
    }
  };

  return globalAdapter;
}

export function getLocale(element) {
  const closestElement = element.closest('[lang]');
  const locale = closestElement && closestElement.lang.split('-')[0];

  if (window.stzhComponents.supportedLocales.indexOf(locale) === -1 || !locale) {
    return window.stzhComponents.defaultLocale;
  } else {
    return locale;
  }
}

export async function fetchTranslations(host, component) {
  const locale = getLocale(host);
  const existingTranslations = JSON.parse(
    sessionStorage.getItem(`stzhComponents.v${window.stzhComponents.version}.i18n.${locale}`)
  );

  if (window.stzhComponents.cacheTranslations && existingTranslations && Object.keys(existingTranslations).length > 0) {
    return {
      ...(existingTranslations[component] || {}),
      $globals: existingTranslations.$globals,
      $locale: existingTranslations.$code,
      $formats: existingTranslations.$formats,
      $formatsIso: existingTranslations.$formatsIso,
      $formatsLegacy: existingTranslations.$formatsLegacy
    };
  }

  try {
    const result = await fetch(`${window.stzhComponents.pathTranslations}/${locale}.json`);

    if (result.ok) {
      const data = await result.json();
      sessionStorage.setItem(`stzhComponents.v${window.stzhComponents.version}.i18n.${locale}`, JSON.stringify(data));
      return {
        ...(data[component] || {}),
        $globals: data.$globals,
        $locale: data.$code,
        $formats: data.$formats,
        $formatsIso: data.$formatsIso,
        $formatsLegacy: data.$formatsLegacy
      };
    }
  } catch (exception) {
    console.error(`Error loading locale: ${locale}`, exception);
  }
}
